<template>
  <div ref="root" class="wb" :class="{ 'wb--tablet': tablet }" :style="rootStyle" data-test="workflow-builder-new">
    <div class="wb-toggle">
      <FollowupsUiToggle
        target="old"
        page-key="workflow"
        note-text="Super Admin: this is the redesigned workflow builder."
        @toggle="$emit('switch-layout', 'old')"
      />
    </div>

    <!-- ============================ header ============================ -->
    <header class="wb-head">
      <button type="button" class="wb-iconbtn" aria-label="Back to workflows" title="Back to workflows" data-test="wf-back" @click="goBack">
        <WfIcon name="arrowL" :size="18" :stroke="2" />
      </button>
      <button type="button" class="wb-crumb" data-test="wf-breadcrumb" @click="goBack">Workflows</button>
      <span class="wb-sep" aria-hidden="true">/</span>

      <!-- No name typed: the trigger IS the workflow, so its label is the title and the box stays optional. -->
      <span v-if="showTitle" class="wb-title" :title="triggerTitle" data-test="wf-title">{{ triggerTitle }}</span>

      <div class="wb-name" :class="{ 'wb-name--aside': showTitle }">
        <input
          v-model="workflowName"
          type="text"
          class="wb-name__input"
          :class="{ 'is-error': !!nameProblem }"
          placeholder="Name (optional)"
          aria-label="Workflow name"
          :aria-invalid="nameProblem ? 'true' : null"
          aria-describedby="wb-name-tip"
          autocomplete="off"
          data-test="wf-name-input"
          @input="setDirty"
          @focus="nameFocus = true"
          @blur="nameFocus = false"
        >
        <div
          v-if="showNameTip"
          id="wb-name-tip"
          class="wb-tip"
          :class="{ 'wb-tip--error': !!nameProblem }"
          role="status"
          data-test="wf-name-tip"
        >
          <span class="wb-tip__icon"><WfIcon :name="nameProblem ? 'alert' : 'info'" :size="14" :stroke="2.1" /></span>
          <span>{{ nameProblem || nameHint }}</span>
          <span class="wb-tip__count">{{ nameLength }}/{{ nameMax }}</span>
        </div>
      </div>

      <span v-if="!loadFailed" class="wb-badge" :class="`wb-badge--${statusTone}`" data-test="wf-status">
        <span class="wb-badge__dot"></span>{{ statusText }}
      </span>

      <div class="wb-actions">
        <span v-if="saveReason" class="wb-why" role="status" data-test="wf-save-reason">
          <WfIcon name="alert" :size="14" :stroke="2.1" class="wb-why__icon" />
          <span class="wb-why__text">{{ saveReason }}</span>
        </span>
        <span v-else-if="isDirty" class="wb-dirty" data-test="wf-dirty-note"><span class="wb-dirty__dot"></span>Unsaved changes</span>

        <div class="wb-reset">
          <button type="button" class="wb-btn wb-btn--ghost" :disabled="isLoading" data-test="wf-reset-btn" @click="resetWorkflow">
            <WfIcon name="rotate" :size="16" :stroke="2" />
            Reset
          </button>
          <div
            v-if="resetAsk"
            class="wb-ask wb-ask--reset"
            role="alertdialog"
            aria-label="Reset this workflow?"
            data-test="wf-reset-ask"
            @keydown.esc.stop="cancelReset"
          >
            <div class="wb-ask__title">Reset this workflow?</div>
            <div class="wb-ask__text">Your unsaved changes will be lost.</div>
            <div class="wb-ask__actions">
              <button ref="resetCancel" type="button" class="wb-btn wb-btn--ghost wb-btn--sm" data-test="wf-reset-cancel" @click="cancelReset">Cancel</button>
              <button type="button" class="wb-btn wb-btn--danger wb-btn--sm" data-test="wf-reset-confirm" @click="confirmReset">Reset</button>
            </div>
          </div>
        </div>

        <button type="button" class="wb-btn wb-btn--primary" :disabled="saveDisabled" data-test="wf-save-btn" @click="saveWorkflow">Save workflow</button>

        <button
          v-if="isEditMode && canActivate"
          type="button"
          class="wb-btn wb-btn--secondary"
          :disabled="isLoading"
          data-test="wf-activate-btn"
          @click="activateWorkflow"
        >Activate</button>
      </div>

      <div
        v-if="pendingLeave"
        class="wb-ask wb-ask--leave"
        role="alertdialog"
        aria-label="Leave without saving?"
        data-test="wf-leave-ask"
        @keydown.esc.stop="cancelLeave"
      >
        <div class="wb-ask__title">Leave without saving?</div>
        <div class="wb-ask__text">Your unsaved changes will be lost.</div>
        <div class="wb-ask__actions">
          <button ref="leaveStay" type="button" class="wb-btn wb-btn--ghost wb-btn--sm" data-test="wf-leave-cancel" @click="cancelLeave">Stay</button>
          <button type="button" class="wb-btn wb-btn--danger wb-btn--sm" data-test="wf-leave-confirm" @click="confirmLeave">Leave</button>
        </div>
      </div>
    </header>

    <div class="wb-alerts"><ErrorDisplay /></div>

    <!-- ============================ a chain: classic only ============================ -->
    <div v-if="classicReason" class="wb-classic" data-test="wf-needs-classic">
      <span class="wb-classic__tile"><WfIcon name="workflow" :size="24" :stroke="1.8" /></span>
      <div class="wb-classic__title">This workflow needs the classic builder</div>
      <div class="wb-classic__text">It runs its actions in a different order than this builder can draw, so it opens in the classic one and keeps working as it does today.</div>
    </div>

    <!-- ============================ palette + canvas ============================ -->
    <div v-else class="wb-body">
      <BuilderPalette
        class="wb-pal"
        :tab="activeTab"
        :triggers="paletteTriggers"
        :actions="paletteActions"
        :placed="placedInfo"
        :all-used="triggerModel.allUsed"
        :used-workflows="usedWorkflowCards"
        :show-lead="showLead"
        :busy="!requiredReady || loadFailed"
        :href-for="workflowHref"
        :settings-href="settingsHref"
        @change-tab="activeTab = $event"
        @place="placeBlock"
        @drag-start="onPaletteDragStart"
        @drag-end="endDrag"
        @open-workflow="openOtherWorkflow"
      />
      <BuilderCanvas
        class="wb-canvas"
        :scene="scene"
        :drag-kind="dragKind"
        :hot="canvasHot"
        :removing-id="removeAsk"
        @measure="canvasWidth = $event"
        @hot="canvasHot = $event"
        @drop="onCanvasDrop"
        @open="openBlock"
        @ask-remove="confirmDeleteNode"
        @cancel-remove="cancelRemove"
        @confirm-remove="confirmRemove"
        @back="goBack"
        @retry="retryLoad"
      />
    </div>

    <BlockSettingsSheet v-if="showSettings" />

    <CommonOverlay :show="isLoading" title="Processing" message="Please wait..." />

    <!-- A save / activate result, one at a time (replaces the toasts). -->
    <WorkflowNotice :notice="notice" @action="runNoticeAction" @close="closeNotice" />
  </div>
</template>

<script>
/**
 * The redesigned Workflow BUILDER shell (design "Workflows UI redesign", frames 1c-1f, 1r, 1u; Workflow
 * overhaul P3, 2026-10-10). Resume doc: Documents/workflow-ui-overhaul.md.
 *
 * `extends` the classic builder (components/WorkflowBuilder.vue), which is NOT edited. EVERY rule is the
 * classic code, inherited: loading a workflow, the nodes / connections model, saving (FlowyText + the Step
 * Functions conversion), the option lists and rule methods the settings sheet reads. Only the shell is new:
 * the header, the palette (BuilderPalette), the canvas (BuilderCanvas) and a few overrides below.
 *
 * THE CANVAS IS A FAN-OUT, NEVER FREE-DRAWN (Nilay, 2026-10-10). The data stays the classic `nodes` +
 * `connections`, but after every structural change `syncGraph()` rebuilds `connections` as "trigger -> each
 * action" (src/utils/workflow/canvasGraph.js) and writes the tidy positions into `x` / `y`, so the classic
 * builder opens the same workflow tidy too. `syncGraph()` never marks the workflow dirty.
 *
 * A workflow that was saved as a CHAIN (an action hanging off another action) is not rewritten: loadWorkflow
 * emits `needs-classic` and the page stops (syncGraph and saveWorkflow do nothing from then on). The route
 * wrapper that shows the classic builder instead is a later step.
 *
 * The page ALSO offers, for the later route wrapper:
 *   - `requestLeave(next)`: the inline "Leave without saving?" for a router leave guard (no browser dialog).
 *   - emits `switch-layout` ('old') for the Super Admin "Classic view" link, and `needs-classic` (reason).
 *
 * CONTRACT WITH THE SETTINGS SHEET (BlockSettingsSheet.vue, built separately): it takes no props and injects
 * `wfBuilder` (this page). It reads selectedNodeData / selectedNode / currentTrigger / needsEmptyProcCodesConfirm
 * and every option list and rule method, all inherited unchanged, and calls closeSettings() (inherited),
 * commitSettings() and removeSelectedNode() (here).
 *
 * No browser dialog anywhere: Reset, leaving with unsaved changes and removing a block all ask inline.
 *
 * NOTICES (P6, 2026-10-10). No toast is drawn from this page. The classic save reports its four results through
 * ONE seam, `notifySaveResult(kind, message, options)` (a toast in the classic builder, exactly as before); this
 * page overrides it to draw a WorkflowNotice with the design's wording: an ok note for "saved" / "deployed", an
 * amber card with "Activate" for "not-deployed", a red card with "Try again" for "failed". Activating draws its
 * own. The classic validation toasts are unreachable from here (the Save button is off for each of them).
 * A workflow that cannot be read sets `loadFailed`: the canvas says so with "Try again" / "Back to workflows",
 * the palette is inert, and placing blocks and saving are refused.
 *
 * ⛔ Props: only `openTab` here (+ the classic `workflowID`). Never one named patNum / siteID / loading /
 * allSettings / isSuperAdmin / userID: the mixin's computed of that name would silently replace it.
 */
import WorkflowBuilder from '../components/WorkflowBuilder.vue';
import ErrorDisplay from '@/components/ErrorDisplay/ErrorDisplay';
import CommonOverlay from '@/components/CommonOverlay';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { collectWorkflowSmsIssues } from '../libraries/validate-workflow-sms';
import { triggerStates } from '@/utils/workflow/triggerStates.js';
import {
  isFanOut, deriveFanOutConnections, layout, triggerNode, orderedActions,
} from '@/utils/workflow/canvasGraph.js';
import { nameError, saveBlocker, autoName, isAutoName, NAME_MAX } from '@/utils/workflow/saveBlockers.js';
import { blockPreview } from '@/utils/workflow/ruleSummary.js';
import {
  triggerByType, triggerByBlockType, triggerLabel, triggerIcon, actionByType, actionLabel, actionIcon,
} from '@/utils/workflow/blockCatalog.js';
import WfIcon from './WfIcon.vue';
import BuilderPalette from './BuilderPalette.vue';
import BuilderCanvas from './BuilderCanvas.vue';
import BlockSettingsSheet from './BlockSettingsSheet.vue';
import WorkflowNotice from './WorkflowNotice.vue';
import noticeHost from './noticeHost.js';

const RETRY_HINT = 'Check your internet connection, then try again.';

/** At or below this root width the palette is 280px and blocks are 240px (the design's `tab` flag, frame 1u). */
const TABLET_MAX = 1100;
const NODE_WIDTH = 260;
const NODE_WIDTH_TABLET = 240;
/** Block heights from the design: an action, a plain trigger, a long-titled one, one with a settings line. */
const ACTION_HEIGHT = 100;
const TRIGGER_HEIGHT = 66;
const TRIGGER_HEIGHT_LONG = 84;
const TRIGGER_HEIGHT_SETTINGS = 100;
const TRIGGER_HEIGHT_SETTINGS_LONG = 118;
const SLOT_HEIGHT = 76;
const LONG_LABEL = 24;
/** A stand-in for the WHEN block while the canvas has actions but no trigger (so the row keeps its place). */
const TRIGGER_SLOT_TYPE = '__wf_trigger_slot';

function curve(x1, y1, x2, y2) {
  return `M${x1} ${y1} C${x1} ${y1 + 44} ${x2} ${y2 - 44} ${x2} ${y2}`;
}

export default {
  name: 'WorkflowBuilderPage',

  extends: WorkflowBuilder,

  mixins: [noticeHost],

  components: {
    WfIcon, BuilderPalette, BuilderCanvas, BlockSettingsSheet, FollowupsUiToggle, ErrorDisplay, CommonOverlay, WorkflowNotice,
  },

  props: {
    /** Which palette tab to open on: 'actions' or 'triggers'. Passed in memory by the router, never in the URL. */
    openTab: { type: String, default: '' },
  },

  provide() {
    return { wfBuilder: this };
  },

  data() {
    return {
      /** The canvas' measured width (BuilderCanvas) and this page's own, for the tablet size. 0 = not measured. */
      canvasWidth: 0,
      rootWidth: 0,
      observer: null,
      /**
       * The height that fits the page between its own top (below the app's header and any banner) and the bottom of
       * the window, in px. 0 = not measured: the CSS `100vh` applies. A flat 100vh starts BELOW the app chrome, so
       * it ran past the bottom of the window and gave the page a second scrollbar (found in a real browser).
       */
      fitHeight: 0,
      fitObserver: null,
      /** What the palette is dragging (a block type) and whether it is over the canvas. */
      dragType: '',
      canvasHot: false,
      /** Show the empty "Drag an action here" slot even though the workflow has actions (right after opening one). */
      slotHint: false,
      nameFocus: false,
      /** Reset's inline ask, the leave ask ({ kind: 'list' | 'workflow' | 'route', ... }) and the id of the block being removed. */
      resetAsk: false,
      pendingLeave: null,
      removeAsk: null,
      /** Why the loaded workflow is not a fan-out ('' = it is, or none is loaded). */
      classicReason: '',
      /** The id of the workflow this tab just created, so the next Save updates it instead of creating another. */
      createdWorkflowID: null,
      /** The site's workflows (which triggers are taken) have been fetched. */
      requiredReady: false,
      /** The workflow this page was opened on could not be read (not found, or the request failed). */
      loadFailed: false,
      /**
       * The stored name when it is one this page made from the trigger (saveBlockers `autoName`): the box shows
       * empty and the header shows the trigger's label instead. '' when the workflow has a typed name, or none yet.
       */
      autoNamed: '',
    };
  },

  computed: {
    // A workflow created in this tab is an edit from then on: classic stays on the create route after the
    // first save, so without this a second Save would create a second workflow.
    isEditMode() {
      return !!(WorkflowBuilder.computed.isEditMode.call(this) || this.createdWorkflowID);
    },

    effectiveWorkflowID() {
      return WorkflowBuilder.computed.effectiveWorkflowID.call(this) || this.createdWorkflowID || null;
    },

    // ---- size ---------------------------------------------------------------------------------
    rootStyle() {
      return this.fitHeight > 0 ? { height: `${this.fitHeight}px` } : null;
    },

    tablet() {
      return this.rootWidth > 0 && this.rootWidth <= TABLET_MAX;
    },

    nodeWidth() {
      return this.tablet ? NODE_WIDTH_TABLET : NODE_WIDTH;
    },

    // ---- the graph ----------------------------------------------------------------------------
    placedTrigger() {
      return triggerNode(this.nodes, this.isTriggerNode);
    },

    actionNodes() {
      return orderedActions(this.nodes, this.isTriggerNode);
    },

    triggerHeight() {
      const t = this.placedTrigger;
      if (!t) return this.actionNodes.length ? SLOT_HEIGHT : TRIGGER_HEIGHT;
      const long = triggerLabel(t.type).length > LONG_LABEL;
      if (this.nodeHasSettings(t.type)) return long ? TRIGGER_HEIGHT_SETTINGS_LONG : TRIGGER_HEIGHT_SETTINGS;
      return long ? TRIGGER_HEIGHT_LONG : TRIGGER_HEIGHT;
    },

    showActionSlot() {
      if (!this.placedTrigger) return false;
      if (this.actionNodes.length === 0 || (this.canvasHot && this.dragKind === 'action')) return true;
      // The idle hint after opening a workflow is only a hint: never let it be the thing that makes the
      // row wider than the canvas (a sideways scrollbar for a two-action workflow on a laptop).
      return this.slotHint && this.slotHintFits;
    },

    /** The action row with one more box (the hint slot) still fits the measured canvas (canvasGraph: 32 gap, 16 margin). */
    slotHintFits() {
      if (this.canvasWidth <= 0) return true;
      const boxes = this.actionNodes.length + 1;
      return boxes * this.nodeWidth + (boxes - 1) * 32 + 2 * 16 <= this.canvasWidth;
    },

    dragKind() {
      if (!this.dragType) return '';
      return this.isTriggerNode(this.dragType) ? 'trigger' : 'action';
    },

    /** What the canvas draws (see BuilderCanvas). */
    scene() {
      const nw = this.nodeWidth;
      const trigger = this.placedTrigger;
      const actions = this.actionNodes;

      if (this.loadFailed) {
        return { mode: 'load-failed', contentWidth: this.canvasWidth, contentHeight: 0, blocks: [], edges: [], slot: null };
      }

      if (this.nodes.length === 0) {
        return {
          mode: this.triggerModel.allUsed ? 'none-left' : 'empty',
          contentWidth: this.canvasWidth,
          contentHeight: 0,
          blocks: [],
          edges: [],
          slot: null,
        };
      }

      // Actions with no trigger: a dashed WHEN box holds the trigger's place until one is dropped.
      const nodes = trigger ? this.nodes : [{ id: TRIGGER_SLOT_TYPE, type: TRIGGER_SLOT_TYPE }].concat(this.nodes);
      const isTrigger = type => type === TRIGGER_SLOT_TYPE || this.isTriggerNode(type);
      const lay = layout(nodes, isTrigger, {
        canvasWidth: this.canvasWidth,
        nodeWidth: nw,
        triggerHeight: this.triggerHeight,
        actionHeight: ACTION_HEIGHT,
        showSlot: this.showActionSlot,
      });

      const blocks = [];
      if (trigger && lay.positions[trigger.id]) blocks.push(this.blockFor(trigger, lay.positions[trigger.id], this.triggerHeight, 'trigger'));
      actions.forEach((a) => {
        if (lay.positions[a.id]) blocks.push(this.blockFor(a, lay.positions[a.id], ACTION_HEIGHT, 'action'));
      });

      const rootPos = lay.positions[trigger ? trigger.id : TRIGGER_SLOT_TYPE];
      const edges = [];
      let slot = null;
      if (rootPos) {
        const x1 = rootPos.x + nw / 2;
        const y1 = rootPos.y + this.triggerHeight;
        actions.forEach((a) => {
          const p = lay.positions[a.id];
          if (p) edges.push({ key: `e${a.id}`, d: curve(x1, y1, p.x + nw / 2, p.y), dashed: !trigger });
        });
        if (!trigger) {
          slot = { kind: 'trigger', x: rootPos.x, y: rootPos.y, w: nw, h: this.triggerHeight };
        } else if (lay.slot) {
          slot = { kind: 'action', x: lay.slot.x, y: lay.slot.y, w: nw, h: SLOT_HEIGHT };
          edges.push({ key: 'slot', d: curve(x1, y1, lay.slot.x + nw / 2, lay.slot.y), dashed: true });
        }
      }

      return {
        mode: 'graph',
        contentWidth: lay.contentWidth,
        contentHeight: lay.contentHeight,
        blocks,
        edges,
        slot,
      };
    },

    // ---- palette ------------------------------------------------------------------------------
    triggerModel() {
      return triggerStates({
        triggers: this.triggers,
        existingWorkflows: this.existingWorkflows,
        currentWorkflowID: this.effectiveWorkflowID,
        placedTriggerType: this.placedTrigger ? this.placedTrigger.type : '',
        mangoVoiceEnabled: this.mangoVoiceEnabled,
      });
    },

    paletteTriggers() {
      return this.triggerModel.items.map((item) => {
        const entry = triggerByType(item.type);
        return {
          type: item.type,
          label: entry ? entry.label : item.label,
          description: entry ? entry.description : '',
          icon: triggerIcon(item.type),
          state: item.state,
          usedBy: item.usedBy,
        };
      });
    },

    paletteActions() {
      return this.actions.map(a => ({
        type: a.type,
        label: actionLabel(a.type),
        description: (actionByType(a.type) || {}).description || '',
        icon: actionIcon(a.type),
        state: !a.enabled ? 'soon' : (this.placedTrigger ? 'available' : 'inert'),
      }));
    },

    placedInfo() {
      const t = this.placedTrigger;
      if (!t) return null;
      const saved = this.workflowData && this.workflowData.TriggerOn ? triggerByBlockType(this.workflowData.TriggerOn) : null;
      return { type: t.type, label: triggerLabel(t.type), mine: this.isEditMode && !!saved && saved.type === t.type };
    },

    usedWorkflowCards() {
      return this.triggerModel.usedWorkflows.map(w => ({
        WorkflowID: w.WorkflowID,
        Name: w.Name,
        triggerLabel: triggerLabel(w.triggerType),
        icon: triggerIcon(w.triggerType),
      }));
    },

    // New workflow, trigger placed, no action yet: the palette points on to the Actions tab.
    showLead() {
      return !this.isEditMode && !!this.placedTrigger && this.actionNodes.length === 0;
    },

    settingsHref() {
      try {
        const resolved = this.$router && this.$router.resolve({ name: 'Settings' });
        return resolved && resolved.href ? resolved.href : '';
      } catch (e) {
        return '';
      }
    },

    // ---- header -------------------------------------------------------------------------------
    nameLength() {
      return (this.workflowName || '').length;
    },

    nameMax() {
      return NAME_MAX;
    },

    nameHint() {
      return `Optional. Letters, numbers, dashes or underscores. Up to ${NAME_MAX} characters.`;
    },

    /** The placed trigger's label, or ''. */
    triggerTitle() {
      return this.placedTrigger ? triggerLabel(this.placedTrigger.type) : '';
    },

    /** The header shows the trigger as the title while nothing is typed in the box. */
    showTitle() {
      return !!this.triggerTitle && !(this.workflowName || '').trim();
    },

    /**
     * What a notice calls this workflow: the typed name, or the trigger's label when the name is blank or the
     * one this page made from the trigger (so a save never says "Saved appointment-scheduled.").
     */
    displayName() {
      const typed = (this.workflowName || '').trim();
      if (typed && !(this.autoNamed && typed === this.autoNamed)) return typed;
      return this.triggerTitle || 'this workflow';
    },

    otherNames() {
      const self = this.effectiveWorkflowID === null || this.effectiveWorkflowID === undefined ? null : String(this.effectiveWorkflowID);
      return (this.existingWorkflows || [])
        .filter(w => w && w.Name && (self === null || String(w.WorkflowID) !== self))
        .map(w => w.Name);
    },

    nameProblem() {
      return nameError(this.workflowName, this.otherNames);
    },

    showNameTip() {
      return !!this.nameProblem || (this.nameFocus && !(this.workflowName || '').trim());
    },

    /** Per node id, how many of its texts are too long to send (collectWorkflowSmsIssues). */
    longTextByNode() {
      const counts = {};
      collectWorkflowSmsIssues(this.nodes).blocks.forEach((b) => {
        counts[String(b.nodeId)] = (counts[String(b.nodeId)] || 0) + 1;
      });
      return counts;
    },

    hasLongText() {
      return Object.keys(this.longTextByNode).length > 0;
    },

    saveReason() {
      if (this.loadFailed) return 'Couldn’t open this workflow';
      return saveBlocker({
        name: this.workflowName,
        otherNames: this.otherNames,
        hasTrigger: !!this.placedTrigger,
        actionCount: this.actionNodes.length,
        hasLongText: this.hasLongText,
      });
    },

    saveDisabled() {
      return !!this.saveReason || !this.isDirty || this.isLoading || !!this.classicReason || this.loadFailed;
    },

    statusText() {
      if (!this.isEditMode) return 'Not saved yet';
      return this.workflowData && this.workflowData.Active ? 'Active' : 'Paused';
    },

    statusTone() {
      return this.statusText === 'Active' ? 'teal' : 'neutral';
    },
  },

  watch: {
    resetAsk(open) {
      if (open) this.focusLater('resetCancel');
    },

    pendingLeave(open) {
      if (open) this.focusLater('leaveStay');
    },
  },

  created() {
    if (this.openTab === 'actions' || this.openTab === 'triggers') this.activeTab = this.openTab;
  },

  mounted() {
    if (this.$el && this.$el.clientWidth) this.rootWidth = this.$el.clientWidth;
    if (typeof window !== 'undefined' && typeof window.ResizeObserver === 'function' && this.$el) {
      this.observer = new window.ResizeObserver(() => {
        if (this.$el) this.rootWidth = this.$el.clientWidth || 0;
      });
      this.observer.observe(this.$el);
    }
    this.fitToWindow();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.fitToWindow);
      // A banner above the page appearing or going away moves its top, and that changes the document's height.
      if (typeof window.ResizeObserver === 'function' && document.body) {
        this.fitObserver = new window.ResizeObserver(() => this.fitToWindow());
        this.fitObserver.observe(document.body);
      }
    }
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.fitObserver) {
      this.fitObserver.disconnect();
      this.fitObserver = null;
    }
    if (typeof window !== 'undefined') window.removeEventListener('resize', this.fitToWindow);
  },

  methods: {
    // ---- layout -------------------------------------------------------------------------------
    /** Size the page to the room between its top and the bottom of the window (never under the 560px the CSS keeps). */
    fitToWindow() {
      const el = this.$el;
      if (!el || typeof window === 'undefined' || typeof el.getBoundingClientRect !== 'function') return;
      const top = el.getBoundingClientRect().top + (window.pageYOffset || 0);
      const room = Math.floor((window.innerHeight || 0) - top);
      const next = room >= 560 ? room : 0;
      if (next !== this.fitHeight) this.fitHeight = next;
    },

    // ---- load ---------------------------------------------------------------------------------
    async loadRequiredData() {
      try {
        await WorkflowBuilder.methods.loadRequiredData.call(this);
      } finally {
        this.requiredReady = true;
      }
    },

    async loadWorkflow() {
      const opening = this.effectiveWorkflowID;
      this.loadFailed = false;
      // The classic load only toasts when the workflow is not in the list ("not found") or the request throws, and
      // leaves `workflowData` as it was. Clear it first, so that afterwards "no workflowData" means "nothing was
      // read" in both cases (a reload after Reset or a first save would otherwise still hold the old copy).
      if (opening) this.workflowData = null;
      await WorkflowBuilder.methods.loadWorkflow.call(this);
      if (opening && !this.workflowData) {
        this.loadFailed = true;
        return;
      }
      const verdict = isFanOut(this.nodes, this.connections, this.isTriggerNode);
      if (!verdict.ok) {
        this.classicReason = verdict.reason;
        this.$emit('needs-classic', verdict.reason);
        return;
      }
      this.classicReason = '';
      this.syncGraph();
      this.slotHint = !!this.placedTrigger;
      this.adoptAutoName();
    },

    /** A loaded name that is the trigger's slug was made by this page: show the trigger instead, keep the box empty. */
    adoptAutoName() {
      if (this.triggerTitle && isAutoName(this.workflowName, this.triggerTitle)) {
        this.autoNamed = this.workflowName;
        this.workflowName = '';
      } else {
        this.autoNamed = '';
      }
    },

    /** "Try again" on the canvas after a workflow could not be read: fetch the site's workflows afresh and open it. */
    async retryLoad() {
      if (this.isLoading) return;
      this.requiredReady = false;
      try {
        this.existingWorkflows = [];
        await this.loadWorkflow();
      } finally {
        this.requiredReady = true;
      }
    },

    // ---- the graph ----------------------------------------------------------------------------
    /**
     * Rebuild `connections` as trigger -> each action and write the tidy positions into every node. Called
     * after each structural change. It never marks the workflow dirty, and it does nothing for a workflow
     * that has to open in the classic builder (it would turn a chain into a fan-out).
     */
    syncGraph() {
      if (this.classicReason) return;
      this.connections = deriveFanOutConnections(this.nodes, this.isTriggerNode);
      this.nextConnectionId = this.connections.length + 1;
      const lay = layout(this.nodes, this.isTriggerNode, {
        canvasWidth: this.canvasWidth,
        nodeWidth: this.nodeWidth,
        triggerHeight: this.triggerHeight,
        actionHeight: ACTION_HEIGHT,
      });
      this.nodes.forEach((n) => {
        const p = lay.positions[n.id];
        if (p) {
          n.x = p.x;
          n.y = p.y;
        }
      });
    },

    /** The canvas block for a node: label / icon from the catalog, the settings line, the red state for long texts. */
    blockFor(node, pos, height, kind) {
      const hasSettings = this.nodeHasSettings(node.type);
      const pv = blockPreview(node, { isTrigger: this.isTriggerNode, boardName: this.boardNameOf });
      const tooLong = this.longTextByNode[String(node.id)] || 0;
      let preview = pv.text ? { text: pv.text, tone: pv.tone } : null;
      if (tooLong) {
        preview = { text: tooLong === 1 ? '1 text is too long to send' : `${tooLong} texts are too long to send`, tone: 'error' };
      }
      return {
        id: node.id,
        kind,
        type: node.type,
        label: kind === 'trigger' ? triggerLabel(node.type) : actionLabel(node.type),
        icon: kind === 'trigger' ? triggerIcon(node.type) : actionIcon(node.type),
        x: pos.x,
        y: pos.y,
        w: this.nodeWidth,
        h: height,
        hasMenu: hasSettings,
        clickable: hasSettings,
        selected: this.showSettings && this.selectedNode === node.id,
        preview,
        removeNote: kind === 'trigger'
          ? (this.actionNodes.length ? 'Its actions stay on the canvas. Add a trigger to start them again.' : '')
          : 'Any settings you set on it are lost.',
      };
    },

    boardNameOf(boardId) {
      const board = (this.kanbanBoards || []).find(b => b.id === boardId);
      return board ? board.name : '';
    },

    // ---- placing blocks -----------------------------------------------------------------------
    /**
     * Put a block on the canvas (a click, Enter / Space or a drop). A trigger only when the palette says it is
     * `available`; an action only when a trigger is on the canvas and the action is enabled. The classic
     * addNode does the adding (its own toasts stay as an unreachable backstop).
     */
    placeBlock(type) {
      if (this.isLoading || !this.requiredReady || this.classicReason || this.loadFailed) return false;
      const isTrigger = this.isTriggerNode(type);
      if (isTrigger) {
        const item = this.triggerModel.items.find(i => i.type === type);
        if (!item || item.state !== 'available') return false;
      } else {
        const action = this.actions.find(a => a.type === type);
        if (!action || !action.enabled || !this.placedTrigger) return false;
      }
      const id = this.addNode(type);
      if (id === null || id === undefined) return false;
      this.syncGraph();
      this.setDirty();
      if (!isTrigger) this.slotHint = false;
      return true;
    },

    onPaletteDragStart(type) {
      this.dragType = type;
    },

    endDrag() {
      this.dragType = '';
      this.canvasHot = false;
    },

    onCanvasDrop(type) {
      const dropped = type || this.dragType;
      this.endDrag();
      this.placeBlock(dropped);
    },

    // ---- opening and removing a block ---------------------------------------------------------
    openBlock(id) {
      const node = this.nodes.find(n => n.id === id);
      if (!node || !this.nodeHasSettings(node.type)) return;
      this.openNodeSettings(node);
    },

    /** Classic asked with a browser dialog; here the canvas draws the ask on the block itself. */
    confirmDeleteNode(nodeId) {
      this.removeAsk = nodeId;
    },

    cancelRemove() {
      this.removeAsk = null;
    },

    confirmRemove(nodeId) {
      this.removeAsk = null;
      if (this.showSettings && this.selectedNode === nodeId) this.closeSettings();
      this.deleteNode(nodeId);
    },

    deleteNode(nodeId) {
      WorkflowBuilder.methods.deleteNode.call(this, nodeId);
      this.syncGraph();
    },

    // ---- the settings sheet's two calls -------------------------------------------------------
    /**
     * "Done" in the sheet: put the edited copy back into `nodes`, dirty the workflow, close the sheet. The
     * classic saveSettings did this behind a browser dialog for an empty Kanban code list; the sheet asks that
     * question itself (needsEmptyProcCodesConfirm) before it calls this.
     */
    commitSettings() {
      const index = this.nodes.findIndex(n => n.id === this.selectedNode);
      if (index !== -1) {
        this.nodes.splice(index, 1, this.selectedNodeData);
        this.setDirty();
      }
      this.closeSettings();
      this.syncGraph();
    },

    saveSettings() {
      this.commitSettings();
    },

    /** "Remove this block" in the sheet: no dialog (the sheet asked), delete the open block and close. */
    removeSelectedNode() {
      const id = this.selectedNode;
      if (id === null || id === undefined) return;
      this.deleteNode(id);
      this.closeSettings();
    },

    // ---- saving -------------------------------------------------------------------------------
    async saveWorkflow() {
      if (this.classicReason) return;
      if (this.loadFailed) return;
      this.syncGraph();
      const wasEdit = this.isEditMode;
      // No name typed: the classic save (and the row, the classic list, the state machine's name) needs one, so
      // the trigger's slug goes in for the save and comes back out after. The stored slug is kept while it still
      // matches the trigger; a trigger swapped since then gets a fresh one (and the old slug is not "taken" by
      // this workflow: otherNames already leaves it out).
      const typed = (this.workflowName || '').trim();
      let generated = '';
      if (!typed) {
        const label = this.triggerTitle;
        if (!label) return;
        generated = isAutoName(this.autoNamed, label) ? this.autoNamed : autoName(label, this.otherNames);
        if (!generated) return;
        this.autoNamed = generated;
        this.workflowName = generated;
      } else {
        this.autoNamed = '';
      }
      try {
        await WorkflowBuilder.methods.saveWorkflow.call(this);
      } finally {
        if (generated) this.workflowName = '';
      }
      // Classic stays on this page after a first save. Remember the new id so the next Save updates it.
      if (!wasEdit && this.workflowSaved && this.workflowData && this.workflowData.WorkflowID !== undefined && this.workflowData.WorkflowID !== null) {
        this.createdWorkflowID = this.workflowData.WorkflowID;
      }
    },

    /**
     * The classic save's one seam for its result (a toast there). Here: the design's wording as a WorkflowNotice.
     * `message` is the classic toast text and `options` is unused; the wording below is the redesign's own.
     */
    notifySaveResult(kind, message, options) {
      const name = this.displayName;
      if (kind === 'saved') {
        this.showNotice({ kind: 'ok', text: `Saved ${name}.` });
      } else if (kind === 'deployed') {
        this.showNotice({ kind: 'ok', text: `Saved ${name}. Changes are live.` });
      } else if (kind === 'not-deployed') {
        this.showNotice({
          kind: 'warning',
          title: 'Saved, but not live yet',
          text: 'Your changes are saved, but the running workflow still uses the old version. Activate to put them live.',
          actionLabel: 'Activate',
        }, () => this.activateWorkflow());
      } else if (kind === 'failed') {
        this.showNotice({
          kind: 'error',
          title: `Couldn’t save ${name}`,
          text: `Your changes are still here. ${RETRY_HINT}`,
          actionLabel: 'Try again',
        }, () => this.saveWorkflow());
      }
    },

    /**
     * The store action raises the app-wide green bar ("Workflow enabled successfully!") by itself. This page
     * draws its own notice, so every enable from here (Activate, and the re-deploy inside the classic save of
     * an active workflow) asks the store to stay quiet. Classic keeps its bar.
     */
    enableWorkflow(payload) {
      return this.$store.dispatch('workflow/enableWorkflow', Object.assign({}, payload, { skipAlertLoading: true }));
    },

    /** Classic leaves the button showing after it worked and the badge on "Paused"; flip both. */
    async activateWorkflow() {
      const workflowID = this.effectiveWorkflowID;
      if (!workflowID) return;
      this.closeNotice();
      this.isLoading = true;
      try {
        await this.enableWorkflow({ WorkflowID: workflowID });
        this.canActivate = false;
        this.workflowData = Object.assign({}, this.workflowData, { Active: 1 });
        this.showNotice({ kind: 'ok', text: `${this.displayName} is on.` });
      } catch (err) {
        console.error('Error activating workflow:', err);
        this.showNotice({
          kind: 'error',
          title: `Couldn’t turn on ${this.displayName}`,
          text: RETRY_HINT,
          actionLabel: 'Try again',
        }, () => this.activateWorkflow());
      } finally {
        this.isLoading = false;
      }
    },

    // ---- Reset --------------------------------------------------------------------------------
    resetWorkflow() {
      this.resetAsk = !this.resetAsk;
    },

    cancelReset() {
      this.resetAsk = false;
    },

    /**
     * Back to the last saved version ("your unsaved changes will be lost"): a saved workflow is read again, a
     * new one goes back to an empty canvas and no name. (The classic Reset emptied the canvas even for a saved
     * workflow.)
     */
    async confirmReset() {
      this.resetAsk = false;
      this.removeAsk = null;
      if (this.showSettings) this.closeSettings();
      this.nodes = [];
      this.connections = [];
      this.nextNodeId = 1;
      this.nextConnectionId = 1;
      this.selectedNode = null;
      this.isDirty = false;
      this.slotHint = false;
      this.classicReason = '';
      if (!this.isEditMode) {
        this.workflowName = '';
        this.autoNamed = '';
        return;
      }
      this.requiredReady = false;
      try {
        // Empty the cache so the read is fresh (a workflow saved in this tab is not in the old copy).
        this.existingWorkflows = [];
        await this.loadWorkflow();
      } finally {
        this.requiredReady = true;
      }
    },

    // ---- leaving ------------------------------------------------------------------------------
    gotoList() {
      this.$router.push({ name: 'Workflow2List' });
    },

    pushWorkflow(id) {
      this.$router.push({ name: 'ViewWorkflow2', params: { workflowID: String(id), openTab: 'actions' } });
    },

    goBack() {
      if (!this.isDirty) {
        this.gotoList();
        return;
      }
      this.pendingLeave = { kind: 'list' };
    },

    /** "Add actions there": open that workflow on its Actions tab (in memory, never in the URL). */
    openOtherWorkflow(id) {
      if (!this.isDirty) {
        this.pushWorkflow(id);
        return;
      }
      this.pendingLeave = { kind: 'workflow', id };
    },

    /** For a router leave guard (the route wrapper): ask inline, then call next() or next(false). */
    requestLeave(next) {
      if (!this.isDirty) {
        next();
        return;
      }
      this.pendingLeave = { kind: 'route', next };
    },

    confirmLeave() {
      const pending = this.pendingLeave;
      this.pendingLeave = null;
      if (!pending) return;
      // They chose to leave: nothing left to protect (and the leave guard must not ask a second time).
      this.isDirty = false;
      if (pending.kind === 'list') this.gotoList();
      else if (pending.kind === 'workflow') this.pushWorkflow(pending.id);
      else if (pending.kind === 'route') pending.next();
    },

    cancelLeave() {
      const pending = this.pendingLeave;
      this.pendingLeave = null;
      if (pending && pending.kind === 'route') pending.next(false);
    },

    workflowHref(workflowID) {
      try {
        const resolved = this.$router.resolve({ name: 'ViewWorkflow2', params: { workflowID: String(workflowID) } });
        return resolved && resolved.href ? resolved.href : '#';
      } catch (e) {
        return '#';
      }
    },

    focusLater(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (el && typeof el.focus === 'function') el.focus();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './workflow-tokens';

.wb {
  @include wf-page-root;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 560px;
  overflow: hidden;
  background: $fu-surface;
}

.wb-toggle {
  flex: none;
  padding: 2px 20px 0;
  background: $fu-surface;

  ::v-deep .fu-uitoggle { margin-bottom: 4px; }
}

// ---- Header ----------------------------------------------------------------------------------
.wb-head {
  position: relative;
  z-index: 6;
  display: flex;
  flex: none;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  min-height: $wb-head-height;
  padding: 0 20px 0 12px;
  background: $fu-surface;
  border-bottom: 1px solid $fu-border;

  @media (max-width: 900px) {
    padding-top: 8px;
    padding-bottom: 8px;
  }
}

.wb-iconbtn {
  @include fu-icon-btn(36px);
}

.wb-crumb {
  @include fu-button-reset;
  @include fu-focus-ring;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  color: $fu-neutral-500;

  &:hover { color: $fu-text; }
}

.wb-sep {
  font-size: 15px;
  color: $fu-neutral-300;
}

// The trigger's label as the title, while the name box is empty.
.wb-title {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: $fu-font-display;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: $fu-text;

  .wb--tablet & { max-width: 200px; }
}

.wb-name {
  position: relative;
  flex: 0 1 340px;
  min-width: 150px;

  // Tablet: a narrower field, so the Save cluster stays on the header's one row at 1024px.
  .wb--tablet & { flex-basis: 240px; }

  // Beside the title: a smaller, quieter box.
  &--aside {
    flex-basis: 220px;
    .wb--tablet & { flex-basis: 170px; }
  }
}

.wb-name__input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid $fu-border;
  border-radius: 10px;
  background: $fu-surface;
  font-family: $fu-font-display;
  font-weight: 800;
  font-size: 17px;
  letter-spacing: -0.01em;
  color: $fu-text;
  outline: none;

  &::placeholder { color: $fu-neutral-400; font-weight: 700; }
  &:focus { border-color: $fu-brand-400; box-shadow: $fu-ring; }
  &.is-error { border-color: $fu-danger; box-shadow: 0 0 0 3px rgba(229, 72, 77, 0.15); }

  .wb-name--aside & {
    font-size: 15px;
    font-weight: 700;
  }
}

.wb-tip {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 20;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: max-content;
  max-width: 400px;
  padding: 9px 12px;
  border: 1px solid $fu-border;
  border-radius: 10px;
  background: $fu-surface;
  color: $fu-neutral-700;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: $fu-shadow-md;

  &--error {
    border-color: $fu-danger-border;
    background: $fu-danger-subtle;
    color: $fu-danger-text;
  }
}

.wb-tip__icon {
  display: flex;
  flex: none;
  margin-top: 1px;
}

.wb-tip__count {
  margin-left: 8px;
  font-variant-numeric: tabular-nums;
  color: $fu-neutral-500;
}

.wb-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 9px;
  border-radius: 999px;
  background: $fu-neutral-100;
  color: $fu-neutral-600;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;

  &--teal {
    background: $fu-brand-50;
    color: $fu-brand-800;
  }
}

.wb-badge__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $fu-neutral-400;

  .wb-badge--teal & { background: $fu-brand-500; }
}

.wb-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
  min-width: 0;
}

// Why Save is off. An amber pill with an icon: as plain grey text beside the button it went unread.
.wb-why {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  height: 30px;
  margin-right: 4px;
  padding: 0 10px;
  border: 1px solid $wf-amber-line;
  border-radius: 999px;
  background: $fu-warning-subtle;
  font-size: 13px;
  font-weight: 700;
  color: $fu-warning-text;
  white-space: nowrap;

  .wb--tablet & { max-width: 190px; }
}

.wb-why__icon { flex: none; }

.wb-why__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wb-dirty {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 4px;
  font-size: 13px;
  font-weight: 600;
  color: $fu-neutral-600;
  white-space: nowrap;
}

.wb-dirty__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $fu-warning;
}

.wb-btn {
  &--ghost { @include fu-btn(ghost, md); height: 40px; }
  &--primary { @include fu-btn(primary, md); height: 40px; }
  &--secondary { @include fu-btn(secondary, md); height: 40px; }
  &--danger { @include fu-btn(danger, md); }
  &--sm { height: 34px; padding: 0 14px; font-size: 13.5px; }
}

.wb-reset { position: relative; }

// ---- Inline asks (Reset, leaving) --------------------------------------------------------------
.wb-ask {
  @include wb-popover(300px);
}

.wb-ask--reset {
  top: calc(100% + 10px);
  right: 0;
}

.wb-ask--leave {
  top: calc(100% - 4px);
  left: 12px;
}

.wb-ask__title {
  font-size: 14px;
  font-weight: 700;
}

.wb-ask__text {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.45;
  color: $fu-neutral-600;
}

.wb-ask__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.wb-alerts {
  flex: none;
  padding: 0 20px;
}

// ---- Body -------------------------------------------------------------------------------------
.wb-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.wb-pal {
  flex: none;
  width: $wb-palette-width;

  .wb--tablet & { width: $wb-palette-width-tablet; }
}

.wb-canvas {
  min-width: 0;
}

.wb-classic {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  text-align: center;
  background: $fu-neutral-25;
}

.wb-classic__tile {
  @include wb-tile(46px, 13px);
  background: $fu-neutral-100;
  color: $fu-neutral-500;
}

.wb-classic__title {
  margin-top: 4px;
  font-size: 17px;
  font-weight: 800;
}

.wb-classic__text {
  max-width: 420px;
  font-size: 13.5px;
  line-height: 1.5;
  color: $fu-neutral-500;
}
</style>
